import { copyOrganizationProfile } from './organization-profile.js';
/**
 * A content boundary for telemetry, not for application data or flag inputs.
 * Free text cannot be made safe by guessing which words describe health.
 * Keep operational fields and numbers; replace content before it reaches a SDK.
 * No Node imports: browser bootstraps use this module too.
 */
const REDACTED = '[REDACTED]';
const keyOf = (key) => key.replace(/[^a-z0-9]/gi, '').toLowerCase();
const PRIVATE_FIELD = /(?:password|secret|token|authorization|cookie|apikey|connectionstring|databaseurl|headers|body|payload|params|parameters|querystring|email|phone|mobile|telephone|address|firstname|lastname|fullname|patientname|customername|contactname|messagepreview|previewtext|transcript|prompt|completion|reasoning|thinking|health|clinical|diagnosis|symptom|notes|description)/;
const CONTENT_FIELDS = new Set([
    'raw',
    'text',
    'requestbody',
    'responsebody',
    'query',
    'sql',
    'detail',
    'hint',
    'name',
]);
const MACHINE_FIELDS = new Set([
    'event',
    'operation',
    'feature',
    'service',
    'logger',
    'context',
    'source',
    'runtime',
    'environment',
    'env',
    'level',
    'severity',
    'type',
    'errortype',
    'exceptiontype',
    'errorname',
    'name',
    'code',
    'errorcode',
    'dbcode',
    'dberrorcode',
    'dbtable',
    'dbcolumn',
    'dbconstraint',
    'table',
    'column',
    'constraint',
    'tablename',
    'columnname',
    'constraintname',
    'errno',
    'method',
    'httpmethod',
    'apimethod',
    'status',
    'provider',
    'model',
    'aimodel',
    'aiprovider',
    'aitask',
    'aigateway',
    'spanname',
    'aispanname',
    'function',
    'module',
    'platform',
    'release',
    'version',
    'appbuild',
    'host',
    'hostname',
    'flyapp',
    'flymachine',
    'grouptype',
    'groupkey',
    'lib',
    'libversion',
    'sdkversion',
    'distinctid',
    'organization',
    'organisation',
    'actor',
    'activityorigin',
    'activityactor',
    'turnkind',
    'trigger',
    'frombuild',
    'tobuild',
    'gateway',
    'role',
    'toolname',
    'functionname',
    'typename',
    'contenttype',
    'clienttype',
    'format',
    'date',
    'startdate',
    'enddate',
    'startsat',
    'endsat',
    'starttime',
    'endtime',
    'timezone',
    'featureflagkey',
    'featureflagkeys',
    'firstdroppedevent',
    'mirroredfrom',
    'sentrysdk',
    'sentryeventid',
    'exceptionlevel',
    'exceptionfingerprint',
    'fingerprint',
    'tags',
    'activityactortype',
    'impersonatedby',
    'posthogloaded',
    'expected',
    'exceptionmechanism',
    'transaction',
    'transactionop',
    'op',
    'mechanism',
    'handled',
    'culprit',
    'filename',
    'abspath',
    'inapp',
    'dt',
    'timestamp',
    'time',
]);
const MACHINE_VALUE = /^[\w$.:/<>@+-]{1,300}$/;
// Property names are data too. A dictionary keyed by a patient name or phone
// must not leak through an otherwise safe boolean/count value. Keep the wire
// schemas and known operational fields; give unknown keys local index labels.
const SCHEMA_FIELDS = new Set(`
  properties metadata extra nested mirror meta fields bindings req request response
  user error err cause message msg value values exception exceptionlist exceptiontype
  exceptionmessage exceptionstacktrace exceptionmechanism exceptionhandled exceptioncode
  exceptionfingerprint exceptionlevel stack stacktrace frames rawstacktrace lineno colno
  precontext postcontext contextline contexts breadcrumbs category data body headers
  cookies querystring query params parameters detail hint raw notes note preview
  messagepreview patientnotes email phone mobile telephone password token accesstoken
  refreshtoken idtoken sessiontoken apikey secret clientsecret authorization setcookie
  content text input output inputstate outputstate outputchoices langfusecontext history
  messages tool tools aitools toolcalls toolcallid toolcallsid tooluseid toolcallscount
  arguments args function toolname role type reasoning thinking systemprompt
  modelparameters usagedetails costdetails temperature maxtokens inputtokens outputtokens
  reasoningtokens cachereadinputtokens cachecreationinputtokens total inputcachedtokens
  aireasoningtokens aiinputtokens aioutputtokens aicachereadinputtokens aicachecreationinputtokens
  aiinput aioutputchoices aiinputstate aioutputstate aitraceid aispanid aiparentid
  aihttpstatus ailatency aierror aiiserror aibaseurl aimodelparameters
  aiinputcostusd aioutputcostusd aitotalcostusd aiinputcost aioutputcost aitotalcost
  duration durationms latency latencyseconds count slotcount slots tokens tokencount bytes
  structuredhistorymessages toolscalled round attempt attempts retries retryable ok success
  cached truncated unserializable self flags featureflags featureflagvalues
  url currenturl initialcurrenturl initialreferrer referrer referer href uri path apipath
  httppath httptarget httpurl urlfull httpmethod httprequestmethod httpstatuscode
  httpresponse statuscode statusmessage apistatus apimethod apirequestid
  requestid planid wrappedby usermessage customeremail reference httpstatus traceid spanid parentspanid parentid sessionid turnid eventid sentryeventid
  userid actoruserid subjectuserid organizationid organization organisation impersonatedby
  leadid patientid contactid customerid conversationid messageid appointmentid serviceid
  branchid locationid graphicid videoid assetid toolcallid externalid campaignid adid
  adaccountid accountid pageid metapageid groupid groupkey grouptype groups group set setonce
  id ids uuid key processpersonprofile impersonation iscustomeractivity activityactortype
  isadmin issystem posthogloaded expected posthogerrorreported errorcode providercode
  reason reasoncode dbcode dberrorcode dbtable dbcolumn dbconstraint tablename columnname constraintname errno
  dbstatement dbquerytext dbsystem dboperationname wrappermessage code status result
  flyapp flymachine host hostname appbuild release version runtime platform environment env
  source logger service servicename device os sdk integrations name options mechanism handled
  trace transactioninfo transactionop op transaction culprit tags fingerprint
  spans starttimestamp
  samplingrate traceflags isremote attributes events links resource instrumentationscope
  starttime endtime duration ended droppedattributescount droppedeventscount droppedlinkscount
  sequence posthoglive logtailconfigured loggerexplicitlyinitialized droppeddelta
  localdroppeddelta synceddelta droppedtotal
  groupset anondistinctid anonymousdistinctid deviceid windowid sessionid insertid
  isidentified lib libversion sdkversion browser browserversion osversion devicetype
  screenheight screenwidth viewportheight viewportwidth pathname referringdomain
  initialpathname initialhost initialreferringdomain initialbrowser initialbrowserversion
  device initialdevice initialdevicetype initialos initialosversion sessionentryreferrer
  sessionentryurl sessionentrypath sessionentryhost sessionduration
  debugmeta images codefile debugfile debugid codeid arch imageaddr imagesize
  deployment deploymenturl buildid frombuild tobuild trigger cachehit requestaborted
  abortcode metacompletenessmissingedge signal expectedabort firstdroppedevent
`
    .split(/\s+/)
    .filter(Boolean));
const PERSONAL_IDS = new Set([
    'id',
    'distinctid',
    'anondistinctid',
    'anonymousdistinctid',
    'userid',
    'actoruserid',
    'subjectuserid',
    'impersonatedby',
    'leadid',
    'patientid',
    'contactid',
    'customerid',
]);
const PROVIDER_IDS = new Set([
    'campaignid',
    'adid',
    'adaccountid',
    'pageid',
    'metapageid',
]);
const phoneShaped = (value) => {
    const digits = value.replace(/\D/g, '').length;
    return /^\+?[\d\s().-]+$/.test(value) && digits >= 7 && digits <= 15;
};
const isContactId = (field, value) => PERSONAL_IDS.has(field) && phoneShaped(String(value));
const knownKey = (key) => {
    const field = keyOf(key);
    return (MACHINE_FIELDS.has(field) ||
        CONTENT_FIELDS.has(field) ||
        SCHEMA_FIELDS.has(field) ||
        /^field_\d+$/.test(key) ||
        /^\$feature\/(?:rollout-|killswitch-)[a-z0-9-]+$/.test(key));
};
/** Drop query, fragment and URL credentials. Route and asset paths still help. */
export function sanitizeTelemetryUrl(value) {
    try {
        const absolute = /^[a-z][a-z\d+.-]*:\/\//i.test(value);
        const url = new URL(value, 'https://telemetry.invalid');
        if (![
            'http:',
            'https:',
            'capacitor:',
            'file:',
            'chrome-extension:',
            'moz-extension:',
            'safari-web-extension:',
            'webkit-masked-url:',
        ].includes(url.protocol))
            return REDACTED;
        const path = url.pathname
            .replace(/\/(reset-password|verify-email|magic-link|invite|invitation|impersonate|token)\/[^/]+/gi, '/$1/[REDACTED]')
            .replace(/\/(start)\/[^/]+/gi, '/$1/[REDACTED]')
            .replace(/\/(forms|t)\/([^/]+)\/[^/]+/gi, '/$1/$2/[REDACTED]')
            .replace(/\/(manage|invitations\/token|mobile-verify|mobile-complete|dial)\/[^/]+/gi, '/$1/[REDACTED]')
            .replace(/[^/]*?(?:@|%40)[^/]*/gi, REDACTED);
        return absolute ? `${url.protocol}//${url.host}${path}` : path;
    }
    catch {
        return REDACTED;
    }
}
/** Preserve exact diagnostic/boot protocol labels, never an untrusted sentence. */
export function sanitizeTelemetryText(value) {
    if (!value)
        return value;
    if ([
        'Database query failed',
        'Operation timed out',
        'Network request failed',
        'telemetry.canary',
        'telemetry.canary: log forwarding is dropping lines',
        'telemetry.canary: no Logtail token \u2014 logs are NOT reaching Better Stack',
        // The split-process CI check and operations runbook consume these exact
        // labels. Match whole messages so adding private text still gets scrubbed.
        'Process role: all (API_ROLE)',
        'Process role: http (API_ROLE)',
        'Process role: jobs (API_ROLE)',
        'Booking lifecycle worker ready',
        'Chatbot flow worker ready',
        'Inbox handoff alert worker ready',
        'Claire classify worker ready',
        'Document match worker ready',
        'Knowledge update worker ready',
        'Lead first-touch worker ready',
        'Voice ingest worker ready',
        'Claire live judge worker ready',
        'Langfuse alert relay worker ready',
        'Claire WhatsApp turn worker ready',
        'Claire WhatsApp outbound worker ready',
        'CRM Granola worker ready (catch-up poll every 5 min)',
        'CRM Granola worker ready (GRANOLA_API_KEY / TWENTY_API_KEY unset: import disabled)',
    ].includes(value))
        return value;
    if (value === REDACTED ||
        ['[TRUNCATED]', '[TRUNCATED: max depth]'].includes(value) ||
        value === '[CIRCULAR]')
        return value;
    if (/failed query|\bSQL\b|\b(?:select|insert into|update|delete from)\b/i.test(value))
        return 'Database query failed';
    if (/\b(?:ECONNREFUSED|ECONNRESET|ETIMEDOUT|ENOTFOUND|EAI_AGAIN)\b/.test(value))
        return (value.match(/\b(?:ECONNREFUSED|ECONNRESET|ETIMEDOUT|ENOTFOUND|EAI_AGAIN)\b/)?.[0] ?? REDACTED);
    if (/\b(?:timeout|timed out)\b/i.test(value))
        return 'Operation timed out';
    if (/\b(?:failed to fetch|fetch failed|network error)\b/i.test(value))
        return 'Network request failed';
    return REDACTED;
}
function stackFrames(value) {
    // The first line and nested cause messages contain arbitrary runtime text.
    // Retain only frame locations, without query strings or URL credentials.
    return value
        .split('\n')
        .filter((line) => /^(?:\s*at\s+(?:[\w$.[\]<>:@/-]+\s+\()?|[\w$.<>]+@)[^\s()]+:\d+(?::\d+)?\)?$/.test(line))
        .map((line) => line.replace(/(?:https?|file|capacitor|chrome-extension|moz-extension):\/\/[^\s)]+/g, sanitizeTelemetryUrl))
        .filter((line) => !/[?&#]|Bearer\s|@[^/\s]+\.[a-z]{2}/i.test(line.replace(/^[\w$.<>]+@/, '')))
        .slice(0, 50)
        .join('\n');
}
const machineString = (value, field = '') => MACHINE_VALUE.test(value) &&
    !/@|Bearer|%40/i.test(value) &&
    (!phoneShaped(value) ||
        PROVIDER_IDS.has(field) ||
        (/^(?:date|startdate|enddate)$/.test(field) &&
            /^\d{4}-\d{2}-\d{2}$/.test(value)))
    ? value
    : REDACTED;
/** Package scope separators in SDK frames are code labels, not email addresses.
 * Keep this exception inside stack frames. All other token bounds still apply. */
const stackFrameString = (value, field) => {
    if (!MACHINE_VALUE.test(value))
        return REDACTED;
    const unscoped = value
        .replace(/(^|\/)@borradh-workspace\+([a-z0-9-]+)@/g, '$1borradh-workspace+$2-')
        .replace(/(^|\/)@borradh-workspace(?=[/.])/g, '$1borradh-workspace');
    return machineString(unscoped, field) !== REDACTED ? value : REDACTED;
};
/** Only controlled diagnostic text may cross the exception boundary. Never
 * preserve arbitrary prose by trying to recognise patient names or conditions.
 * Dynamic identifiers in engine/database messages are discarded, not copied.
 */
function exceptionMessage(value, source) {
    for (const reason of [
        'client closed the connection mid-request',
        'database connection dropped before a queued write, the pool reconnects',
    ]) {
        if (value.startsWith(`Expected behaviour: ${reason}: `))
            return `Expected behaviour: ${reason}`;
        if (value === `Expected behaviour: ${reason}`)
            return value;
    }
    const property = /^Cannot (read|set) propert(?:y|ies) of (undefined|null)(?: \([^\n]*\))?$/.exec(value);
    if (property)
        return `Cannot ${property[1]} properties of ${property[2]}`;
    if (/^[^\n]+ is not a function$/.test(value))
        return 'Value is not a function';
    if (/^[^\n]+ is not defined$/.test(value))
        return 'Variable is not defined';
    if (/^duplicate key value violates unique constraint(?: "[^\n]*")?$/.test(value))
        return 'Unique constraint violation';
    if ([
        'Value is not a function',
        'Variable is not defined',
        'Unique constraint violation',
        'Maximum call stack size exceeded',
        'Invalid time value',
        'Invalid array length',
        'Assignment to constant variable.',
        'Internal server error',
        'Service unavailable',
    ].includes(value) ||
        /^Request failed with status code [1-5]\d{2}$/.test(value) ||
        /^Failed to (?:create|update|delete|load|fetch|save) (?:appointment|lead|contact|conversation|message|organization|user|service|offer|campaign|document|file|video|image)$/.test(value))
        return value;
    const connectionCode = /\b(?:CONNECT_TIMEOUT|CONNECTION_CLOSED|CONNECTION_ENDED)\b/.exec(value)?.[0];
    if (connectionCode)
        return connectionCode;
    const label = sanitizeTelemetryText(value);
    if (label !== REDACTED &&
        !['', '[CIRCULAR]', '[TRUNCATED]', '[TRUNCATED: max depth]'].includes(label))
        return label;
    // Unknown content still yields a useful alert: identify the failing code,
    // using the same source frames we already allow, without its runtime values.
    if (typeof source?.stack === 'string') {
        for (const frame of stackFrames(source.stack).split('\n')) {
            const location = /(?:^|\/)([\w.-]+\.[cm]?[jt]sx?:\d+(?::\d+)?)\)?$/.exec(frame);
            if (location) {
                const fn = /^\s*at ([\w$.<>]+) \(/.exec(frame)?.[1] ??
                    /^[\w$.<>]+(?=@)/.exec(frame)?.[0];
                return `Application error${fn ? ` in ${fn}` : ''} (${location[1]})`;
            }
        }
    }
    const stacktrace = source?.stacktrace;
    if (stacktrace && typeof stacktrace === 'object') {
        const frames = stacktrace.frames;
        if (Array.isArray(frames)) {
            // SDK frames are oldest first. Prefer the closest in-app frame.
            const ordered = frames.slice(-50).reverse();
            for (const frame of [
                ...ordered.filter((entry) => entry?.in_app === true),
                ...ordered.filter((entry) => entry?.in_app !== true),
            ]) {
                if (!frame || typeof frame.filename !== 'string')
                    continue;
                const file = /(?:^|\/)([\w.-]+\.[cm]?[jt]sx?)$/.exec(sanitizeTelemetryUrl(frame.filename))?.[1];
                if (file && Number.isSafeInteger(frame.lineno) && frame.lineno > 0) {
                    const column = Number.isSafeInteger(frame.colno) && frame.colno > 0
                        ? `:${frame.colno}`
                        : '';
                    const fn = typeof frame.function === 'string' &&
                        /^[\w$.<>]+$/.test(frame.function)
                        ? ` in ${frame.function}`
                        : '';
                    return `Application error${fn} (${file}:${frame.lineno}${column})`;
                }
            }
        }
    }
    return 'Application error (details withheld)';
}
function walk(value, key, depth, seen, exceptionRecord = false, stackFrame = false) {
    const field = keyOf(key);
    if (/^field_\d+$/.test(key))
        return REDACTED;
    if (isContactId(field, value))
        return REDACTED;
    // Counters such as input_tokens and usage.input must survive content policy.
    if ((PRIVATE_FIELD.test(field) && field !== 'modelparameters') ||
        CONTENT_FIELDS.has(field)) {
        const metric = /tokens$|tokencount$|count$|bytes$/.test(field) &&
            typeof value === 'number';
        if (!metric && value !== undefined && value !== null)
            return REDACTED;
    }
    if (value === null || value === undefined || typeof value === 'boolean')
        return value;
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : undefined;
    if (typeof value === 'bigint')
        return value.toString();
    if (typeof value === 'string') {
        if (/stack/.test(field))
            return stackFrames(value);
        if (/url$|uri$|href$|referrer$|referer$|^urlfull$|^path$|^apipath$|^httppath$|^httptarget$|^pathname$|^codefile$|^debugfile$/.test(field) ||
            (field === 'transaction' &&
                (value.startsWith('/') || value.includes('://'))))
            return sanitizeTelemetryUrl(value);
        if (MACHINE_FIELDS.has(field) ||
            /id$|ids$|code$/.test(field) ||
            field.endsWith('method') ||
            ['servicename', 'os', 'device'].includes(field) ||
            field.startsWith('feature'))
            return machineString(value, field);
        return sanitizeTelemetryText(value);
    }
    if (typeof value !== 'object')
        return undefined;
    const organizationProfile = copyOrganizationProfile(value);
    if (organizationProfile)
        return organizationProfile;
    if (value instanceof Date)
        return new Date(value.getTime());
    if (depth > 12)
        return '[TRUNCATED: max depth]';
    if (seen.has(value))
        return '[CIRCULAR]';
    seen.add(value);
    try {
        if (value instanceof Error) {
            return {
                name: machineString(value.name),
                message: exceptionMessage(value.message, value),
                stack: stackFrames(value.stack ?? ''),
                ...walk(Object.fromEntries(Object.entries(value).filter(([key]) => !['name', 'message', 'stack', 'cause'].includes(key))), '', depth + 1, seen),
                ...(value.cause !== undefined && {
                    cause: walk(value.cause, 'cause', depth + 1, seen),
                }),
            };
        }
        if (Array.isArray(value))
            return value
                .slice(0, 1000)
                .map((item) => walk(item, key, depth + 1, seen, exceptionRecord, stackFrame));
        const result = {};
        let unknownKey = 0;
        for (const [childKey, child] of Object.entries(value)) {
            // Sentry request fields have object schemas. Omit content rather than
            // replacing an object with a string the ingest endpoint cannot accept.
            if (keyOf(key) === 'request' &&
                ['headers', 'cookies', 'data', 'body', 'querystring'].includes(keyOf(childKey)))
                continue;
            // Dynamic property names can contain personal text too.
            if (!knownKey(childKey)) {
                result[`field_${++unknownKey}`] = REDACTED;
                continue;
            }
            const safeKey = childKey;
            const toolName = childKey === 'name' &&
                (keyOf(key) === 'function' ||
                    ['tool_use', 'tool-call', 'function', 'server_tool_use'].includes(String(value.type)));
            const childField = keyOf(childKey);
            const record = value;
            const isError = exceptionRecord ||
                (['error', 'err', 'cause'].includes(field) &&
                    typeof record.stack === 'string');
            if (stackFrame &&
                ['filename', 'abspath', 'module'].includes(childField) &&
                typeof child === 'string') {
                result[safeKey] = stackFrameString(child, childField);
            }
            else if (isError &&
                ['message', 'value'].includes(childField) &&
                typeof child === 'string') {
                result[safeKey] = exceptionMessage(child, record);
            }
            else if (isError &&
                childField === 'name' &&
                typeof child === 'string') {
                result[safeKey] = machineString(child);
            }
            else if (childField === 'exceptionmessage') {
                const list = record.$exception_list;
                const summary = (message, index) => typeof message === 'string'
                    ? exceptionMessage(message, Array.isArray(list) ? list[index] : undefined)
                    : walk(message, childKey, depth + 1, seen);
                result[safeKey] = Array.isArray(child)
                    ? child.slice(0, 1000).map(summary)
                    : summary(child, 0);
            }
            else {
                result[safeKey] = toolName
                    ? machineString(String(child))
                    : walk(child, childKey, depth + 1, seen, childField === 'exceptionlist' ||
                        (field === 'exception' && childField === 'values'), field === 'stacktrace' && childField === 'frames');
            }
        }
        return result;
    }
    finally {
        seen.delete(value);
    }
}
/** Returns a bounded copy. Never change a model prompt, request or Error in place. */
export function sanitizeTelemetry(value) {
    try {
        return walk(value, '', 0, new WeakSet());
    }
    catch {
        return REDACTED;
    }
}
/** SDKs need an Error instance to retain real stack frames and error grouping. */
export function sanitizeTelemetryError(value, depth = 0) {
    try {
        return copyTelemetryError(value, depth);
    }
    catch {
        const safe = new Error(REDACTED);
        safe.stack = undefined;
        return safe;
    }
}
function copyTelemetryError(value, depth) {
    const source = value && typeof value === 'object'
        ? value
        : undefined;
    const message = typeof source?.message === 'string'
        ? source.message
        : typeof value === 'string'
            ? value
            : '';
    const safe = new Error(exceptionMessage(message, source));
    safe.name =
        typeof source?.name === 'string' ? machineString(source.name) : 'Error';
    // Do not add a synthetic sanitizer stack if the original had none.
    // Sentry's Node parser skips the first line as the Error header. Keep a safe
    // header so the throwing frame survives both the first copy and later ones.
    safe.stack =
        typeof source?.stack === 'string'
            ? `${safe.name}: ${safe.message}\n${stackFrames(source.stack)}`
            : undefined;
    if (source) {
        const fields = sanitizeTelemetry({ ...source });
        for (const [key, field] of Object.entries(fields)) {
            if (!['message', 'name', 'stack', 'cause'].includes(key))
                Object.defineProperty(safe, key, {
                    value: field,
                    enumerable: true,
                    configurable: true,
                });
        }
        if (source.cause !== undefined && depth < 5)
            safe.cause = sanitizeTelemetryError(source.cause, depth + 1);
    }
    return safe;
}
//# sourceMappingURL=telemetry-sanitizer.js.map